import React, { useEffect, useState } from "react";
import { Card, Grid, Text, Button, Badge, Divider, Box, Center, Loader, createStyles } from "@mantine/core";
import CustomBreadcrumbs from "../../components/CustomBreadCrumbs";
import { ServiceTag } from "../Eligibility/ServiceTag";
import { useNavigate } from "react-router-dom";
import { useMedplum } from "@medplum/react";
import { formatAddress, getPatientName } from "../Eligibility/EligibiltyService";



interface PatientDetails {
  relationship: string;
  name: string;
  address: string;
  cityStateZip: string;
  gender: string;
  dateOfBirth: string;
  planNumber: string;
  serviceDate: string;
  eligibilityBegin: string;
  memberId: string;
  subscriberName: string;
  eligibilityStatus: string;
  groupNumber: string;
  eligibilityCoverageDate: string;
  planCoverageDate: string;
  payerType: string;
  Organization: string;
}

interface TransactionDetails {
  transactionId: string;
  customerId: string;
}

interface Props {
  patientDetails: PatientDetails;
}


const useStyles = createStyles(() => ({
  loader: {
    marginTop: "40px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

const PatientDetailsCard = () => {
  
  const navigate = useNavigate();
  const medplum = useMedplum();

  const [isFetching, setIsFetching] = useState(true);
  const [claimRes, setClaimRes] = useState<any>({});
  const [pat, setPat] = useState<any>({});
  const [prov, setProv] = useState<any>({});
  const [payer, setPayer] = useState<any>({});
  const { classes } = useStyles();



  useEffect(() => {
    const location = window.location.href;
    const url = new URL(location);
    const id = url.searchParams.get("id");
    if (!id) {
      navigate("/admin/insurance/prior-auth/new?service=availity");
      return
    }
    getClaimResponse(id);
    
  }, []);

  const getClaimResponse = async (id: string) => {
    const claimResponse = await medplum.readResource("ClaimResponse", id);
    const [patient, provider, payer] = await Promise.all([
      medplum.readReference(claimResponse.patient),
      medplum.readReference(claimResponse.requestor as any),
      medplum.readReference(claimResponse.insurer),
    ])

    setClaimRes(claimResponse);
    setPat(patient);
    setProv(provider);
    setPayer(payer);
    setIsFetching(false);
  }

  const renderRow = (fields: { label: string; value: string }[]) => (
    <Box
      mb={"12px"}
      sx={{ border: "1px solid #eaeaea", padding: "24px", borderRadius: "5px" }}
    >
      <Grid align="center" style={{ padding: "10px 0" }}>
        {fields.map((field, index) => (
          <Grid.Col span={3} key={index} style={{ position: "relative" }}>
            <Text color="#474E5B" weight={500} fs={"14px"}>
              {field.label}
            </Text>
            <Text size={"sm"} weight={600} fs={"14px"} tt={'capitalize'}>
              {field.value}
            </Text>
            {index < fields.length - 1 && (
              <Divider
                orientation="vertical"
                style={{
                  position: "absolute",
                  right: 0,
                  top: "10%",
                  bottom: "10%",
                  marginRight: "20px",
                }}
              />
            )}
          </Grid.Col>
        ))}
      </Grid>
    </Box>
  );

  return (
    <>
      {isFetching ? (
        <Center className={classes.loader}>
          <Loader />
        </Center>
      ) : (<Card padding="lg" radius="md">
      <Grid mb={"12px"}>
        <Box
          sx={{
            marginBottom: "16px",
            justifyContent: "space-between",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: "100%",
          }}
        >
          {" "}
          <CustomBreadcrumbs />
          <ServiceTag serviceType={"availity"} />
        </Box>
        <Grid.Col span={6}>
          <Text sx={{ fontSize: "22px" }} weight={600}>
            Prior Auth
          </Text>
        </Grid.Col>
        <Grid.Col span={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button>Submit Claim</Button>
        </Grid.Col>
      </Grid>
      <Text size="xl" weight={600} mb="12px">
        Patient Details
      </Text>

      <Box>
        {/* Row 1 */}
        {renderRow([
          { label: "Name", value: getPatientName(pat.name) },
          { label: "Date of Birth", value: pat.birthDate},
          { label: "Address", value: pat?.address?.[0]?.line?.join(', ')??'--' },
          { label: "City-State-Zip", value: pat?.address?.[0]?.city + "-" + pat?.address?.[0]?.state + "-" + pat.address?.[0]?.postalCode },
        ])}

        {/* Row 2 */}

        {/* Row 3 */}
        {/* {renderRow([
          { label: "Subscriber Name", value: details.subscriberName },
          { label: "Eligibility Status", value: details.eligibilityStatus },
          { label: "Group Number", value: details.groupNumber },
          { label: "Plan Coverage Date", value: details.planCoverageDate },
        ])} */}
        <Text size="xl" weight={600} mb="12px" mt={24}>
          Request Details
        </Text>
        {renderRow([
          { label: "Insurer", value: payer?.name??'--' },
          { label: "Provider", value: prov?.name??'--' },
          { label: "Claim Type", value: claimRes.type.coding[0].code },
          { label: "Sub Type", value: claimRes.subType.coding[0].display },
        ])}
        {renderRow([
          { label: "Certificate Start", value: claimRes?.preAuthPeriod?.start},
          { label: "Certificate Expiration", value: claimRes?.preAuthPeriod?.end},
        ])}
      </Box>
      <Box mb={24} mt={24}>
        <TransactionCard
          details={{
            transactionId: claimRes?.extension.find((ext: any) => ext.url === "txId")?.valueString ?? '--',
            customerId: claimRes?.extension.find((ext: any) => ext.url === "custId")?.valueString ?? '--',
          }}
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          height: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            padding: "16px",
            borderRadius: "5px",
            backgroundColor: "#FAFAFA",
            border: "1px solid #EDEDED",
          }}
        >
          <Text size="lg" weight={500}>
            Certificate Information
          </Text>
        </Box>
        <Box
          sx={{
            display: "flex",
            padding: "12px",
            borderRadius: "5px",
            border: "1px solid #EDEDED",
            width: "100%",
            height: "auto",
            flexDirection: "column",
          }}
        >
          <Box sx={{ width: "100%", display: "flex", flexDirection: "row" }}>
            <Box
              sx={{
                width: "50%",
                display: "flex",
                flexDirection: "row",
                gap: "12px",
              }}
            >
              <Text weight={500} color="#474E5B" size="14px">
                Reference Number
              </Text>
              <Text> :</Text>
              <Text color="#262930" weight={600} size="14px">
                {claimRes?.preAuthRef??"--"}
              </Text>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                //   alignItems: "flex-end",
                width: "50%",
              }}
            >
              <Badge color="orange" radius={"sm"} size="md">
                {claimRes?.outcome}
              </Badge>
            </Box>
          </Box>

          <Divider sx={{ margin: "16px 0px" }} />
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <Text weight={500} color="#474E5B" size="14px">
              Review reason
            </Text>
            <Text color="#262930" weight={600} size="14px">
              {claimRes?.disposition}
            </Text>
          </Box>
          <Divider sx={{ margin: "16px 0px" }} />
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <Text weight={500} color="#474E5B" size="14px">
              Message
            </Text>
            <Text color="#262930" weight={600} size="14px">
              Your request has been received. Cheack status via the the
              dashboard. Standard turn-round for authorization reviews
              apply. Contact the health plan with question{" "}
            </Text>
          </Box>
        </Box>
      </Box>
    </Card>
)}</>);
};

const TransactionCard: React.FC<{ details: TransactionDetails }> = ({
  details,
}) => {
  return (
    <Card
      padding="lg"
      radius="md"
      style={{
        border: "1px solid #BEDEF8",
        backgroundColor: "#F0F8FF",
        width: "100%",
      }}
    >
      <Grid align="center">
        {/* Transaction ID */}
        {Object.entries(details).map(([key, value]) => (
          <Grid.Col span={4}>
            <Box sx={{ display: "flex", flexDirection: "row", gap: "3px" }}>
              <Text color="#474E5B" weight={500} fs={"14px"} tt={"capitalize"} inline>
                {key.replace("Id", " ID")}
              </Text>
              <Text inline weight={600} fs={"14px"}>
                : {value}
              </Text>
            </Box>
          </Grid.Col>
        ))}
      </Grid>
    </Card>
  );
};

export const PreAuthDetails = () => {
  return (
    <div style={{ padding: "20px" }}>
      <PatientDetailsCard />
    </div>
  );
};




